import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import {
  getAnswerGrades,
  getAnswerOptedOut,
  getGradingOptOut,
  getGradingReflection,
  getGradingRows,
  getGradingScale,
  type TSubmitFormAnswer,
} from '@onesource/schemas';
import type { TFormQuestion } from '@onesource/services';
import { useLayoutEffect, useRef, useState } from 'react';
import { MdCheck } from 'react-icons/md';

import { FORM_CONTENT_MAX_WIDTH } from './rich-content-sx';

const BRAND = '#ff4d6d';

// The matrix's own width, in the same units the grid is built from. The layout
// switches on the width the question ACTUALLY gets rather than on a viewport
// breakpoint: the page has a sidebar and a reading measure, so the viewport says
// little about the room this question has, and a ten-point scale runs out of
// room on a desktop where a three-point scale is still comfortable.
const MATRIX_LABEL_MIN_WIDTH = 190;
const MATRIX_CELL_WIDTH = 48;
const MATRIX_OPT_OUT_WIDTH = 96;
const MATRIX_GAP = 8;
const MATRIX_PADDING = 32;

/** Touch target floor. Below this the scale is a row of targets you miss. */
const STACKED_OPTION_MIN_HEIGHT = 44;

interface MultipleGradingQuestionProps {
  question: TFormQuestion;
  answer: TSubmitFormAnswer;
  onAnswerChange: (answer: TSubmitFormAnswer) => void;
  disabled?: boolean;
}

/**
 * A grid of labelled rows scored on the question's scale, with an optional
 * escape-hatch column beside it ("New to Me").
 *
 * Rendered as checkboxes to match the design but behaving as a radio group: a row
 * carries exactly one answer, so choosing any cell clears the rest of that row.
 * Grades are keyed by the row's stable id rather than its position, so relabelling
 * or reordering a row in the builder never reassigns a member's answer.
 *
 * Too narrow for the grid, each row becomes a block of its own — label on one
 * line, the scale as a row of labelled buttons under it. A matrix squeezed into a
 * phone puts every checkbox column at a width the label can't survive, and the
 * number a member is choosing ends up several rows away from the box they tap.
 */
export const MultipleGradingQuestion = ({
  question,
  answer,
  onAnswerChange,
  disabled = false,
}: MultipleGradingQuestionProps) => {
  const rows = getGradingRows(question.metadata);
  const scale = getGradingScale(question.metadata);
  const optOut = getGradingOptOut(question.metadata);
  const reflection = getGradingReflection(question.metadata);

  const grades = getAnswerGrades(answer.value);
  const optedOut = getAnswerOptedOut(answer.value);

  const points = Array.from({ length: scale.max - scale.min + 1 }, (_, index) => scale.min + index);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  // Measured in a layout effect so the first paint is already in the right
  // layout — a post-paint measurement would flash the grid on every phone.
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }
    setContainerWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const matrixMinWidth =
    MATRIX_LABEL_MIN_WIDTH +
    points.length * MATRIX_CELL_WIDTH +
    (optOut.enabled ? MATRIX_OPT_OUT_WIDTH : 0) +
    (points.length + (optOut.enabled ? 1 : 0)) * MATRIX_GAP +
    MATRIX_PADDING;

  const isStacked = containerWidth !== null && containerWidth < matrixMinWidth;

  /**
   * A row holds exactly one answer, so every choice is written as a whole-row
   * replacement: grading clears any opt-out, opting out clears any grade, and
   * clicking the current selection again clears both.
   */
  const setRow = (rowId: string, choice: { kind: 'grade'; value: number } | { kind: 'optOut' } | { kind: 'clear' }) => {
    const nextGrades = { ...grades };
    delete nextGrades[rowId];
    if (choice.kind === 'grade') {
      nextGrades[rowId] = choice.value;
    }

    const nextOptedOut =
      choice.kind === 'optOut' ? [...new Set([...optedOut, rowId])] : optedOut.filter(id => id !== rowId);

    onAnswerChange({ ...answer, value: { ...answer.value, grades: nextGrades, optedOut: nextOptedOut } });
  };

  const cellSx = { width: MATRIX_CELL_WIDTH, textAlign: 'center' as const, flexShrink: 0 };
  const optOutCellSx = { width: MATRIX_OPT_OUT_WIDTH, textAlign: 'center' as const, flexShrink: 0 };

  /** Shared look for a stacked option, filled in brand once it is the answer. */
  const stackedOptionSx = (isSelected: boolean) => ({
    minHeight: STACKED_OPTION_MIN_HEIGHT,
    borderRadius: '10px',
    border: '1px solid',
    borderColor: isSelected ? BRAND : 'grey.300',
    bgcolor: isSelected ? BRAND : 'background.paper',
    color: isSelected ? 'common.white' : 'text.secondary',
    fontWeight: isSelected ? 700 : 500,
    transition: 'background-color 120ms ease-out, border-color 120ms ease-out, color 120ms ease-out',
    '&.Mui-disabled': { opacity: 0.5 },
    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: BRAND, outlineOffset: '2px' },
  });

  const hasLegend = !!(optOut.description || scale.minLabel || scale.maxLabel);
  const legend = (
    <>
      {optOut.enabled && optOut.description ? `${optOut.label}: = ${optOut.description} ` : ''}
      {scale.minLabel ? (
        <>
          <Box component="span" sx={{ color: BRAND, fontWeight: 700 }}>
            {scale.min}
          </Box>
          {` = ${scale.minLabel} → `}
        </>
      ) : null}
      {scale.maxLabel ? (
        <>
          <Box component="span" sx={{ color: BRAND, fontWeight: 700 }}>
            {scale.max}
          </Box>
          {` = ${scale.maxLabel}`}
        </>
      ) : null}
    </>
  );

  if (rows.length === 0) {
    // Still rendered, so the observer has something to measure if rows arrive.
    return <Box ref={containerRef} />;
  }

  return (
    <Box ref={containerRef} sx={{ maxWidth: FORM_CONTENT_MAX_WIDTH, mx: 'auto' }}>
      <Box sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: '12px', overflow: 'hidden' }}>
        {/* Stacked, every option carries its own number, so a column header would
            only repeat what the buttons already say — the header band carries the
            scale's meaning instead, which otherwise sits a whole question below
            the controls it explains. */}
        {isStacked && hasLegend && (
          <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.100', fontSize: '0.75rem', color: 'text.secondary' }}>
            {legend}
          </Box>
        )}

        {!isStacked && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.25,
              bgcolor: 'grey.100',
              fontSize: '0.8125rem',
              fontWeight: 700,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>Topic</Box>
            {optOut.enabled && <Box sx={optOutCellSx}>{optOut.label}</Box>}
            {points.map(point => (
              <Box key={point} sx={cellSx}>
                {point}
              </Box>
            ))}
          </Box>
        )}

        {rows.map(row => {
          const isOptedOut = optedOut.includes(row.id);

          if (isStacked) {
            return (
              <Box
                key={row.id}
                sx={{
                  px: 2,
                  py: 2,
                  borderTop: '1px solid',
                  borderColor: 'grey.100',
                  '&:first-of-type': { borderTop: 0 },
                }}
              >
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.45, mb: 1.5 }}>
                  {row.label}
                </Typography>

                {/* One answer per row, opt-out included — so the scale and its
                    escape hatch are a single radio group, not two controls that
                    happen to clear each other. */}
                <Box role="radiogroup" aria-label={row.label} sx={{ display: 'flex', gap: 0.75 }}>
                  {points.map(point => {
                    const isSelected = grades[row.id] === point;
                    return (
                      <ButtonBase
                        key={point}
                        role="radio"
                        aria-checked={isSelected}
                        aria-label={`${row.label}: ${point}`}
                        disabled={disabled}
                        onClick={() => setRow(row.id, isSelected ? { kind: 'clear' } : { kind: 'grade', value: point })}
                        sx={{ ...stackedOptionSx(isSelected), flex: 1, fontSize: '1rem' }}
                      >
                        {point}
                      </ButtonBase>
                    );
                  })}
                </Box>

                {optOut.enabled && (
                  <ButtonBase
                    role="radio"
                    aria-checked={isOptedOut}
                    aria-label={`${row.label}: ${optOut.label}`}
                    disabled={disabled}
                    onClick={() => setRow(row.id, isOptedOut ? { kind: 'clear' } : { kind: 'optOut' })}
                    sx={{
                      ...stackedOptionSx(isOptedOut),
                      mt: 0.75,
                      width: '100%',
                      px: 1.5,
                      gap: 1,
                      justifyContent: 'flex-start',
                      fontSize: '0.875rem',
                    }}
                  >
                    {/* A permanently visible tick reads as already-chosen, so the
                        slot is reserved and only filled once it is the answer. */}
                    <Box sx={{ width: 18, display: 'flex', flexShrink: 0 }}>
                      {isOptedOut ? <MdCheck size={18} /> : null}
                    </Box>
                    {optOut.label}
                  </ButtonBase>
                )}
              </Box>
            );
          }

          return (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                borderTop: '1px solid',
                borderColor: 'grey.100',
              }}
            >
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem' }}>{row.label}</Typography>

              {optOut.enabled && (
                <Box sx={optOutCellSx}>
                  <Checkbox
                    size="small"
                    disabled={disabled}
                    checked={isOptedOut}
                    onChange={() => setRow(row.id, isOptedOut ? { kind: 'clear' } : { kind: 'optOut' })}
                    inputProps={{ 'aria-label': `${row.label}: ${optOut.label}` }}
                  />
                </Box>
              )}

              {points.map(point => (
                <Box key={point} sx={cellSx}>
                  <Checkbox
                    size="small"
                    disabled={disabled}
                    checked={grades[row.id] === point}
                    onChange={() =>
                      setRow(row.id, grades[row.id] === point ? { kind: 'clear' } : { kind: 'grade', value: point })
                    }
                    inputProps={{ 'aria-label': `${row.label}: ${point}` }}
                  />
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>

      {!isStacked && hasLegend && (
        <Typography sx={{ mt: 1.5, textAlign: 'center', fontSize: '0.75rem', color: 'text.secondary' }}>
          {legend}
        </Typography>
      )}

      {reflection && (
        <Box
          sx={{
            mt: 4,
            p: 2.5,
            bgcolor: '#fff5f7',
            border: '1px solid',
            borderColor: '#ffd9e0',
            borderRadius: '12px',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1.5 }}>{reflection.title}</Typography>
          {reflection.prompts.map((prompt, index) => (
            <Box
              // Prompts are a fixed ordered list of strings with no id of their own.
              // oxlint-disable-next-line react/no-array-index-key
              key={`${question.id}-prompt-${index}`}
              sx={{ display: 'flex', gap: 1.5, mb: 1.5, '&:last-of-type': { mb: 0 } }}
            >
              <Typography sx={{ color: BRAND, fontWeight: 700, fontSize: '0.875rem', flexShrink: 0 }}>
                {String(index + 1).padStart(2, '0')}.
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>{prompt}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
